import type {uuid} from '../@types/schema';

const PURGE_FLAG_PREFIX = 'autosave.purge.v1';

/**
 * Cutoff for the historical-bloat remediation. After this date, the purge is
 * a no-op for everyone — the original key-mismatch leak is long since fixed,
 * affected users have been remediated by their first post-fix login, and
 * keeping the destructive code path active beyond this date risks clobbering
 * legitimate in-flight crash-recovery autosaves on edge cases (incognito,
 * cleared site data, etc.).
 *
 * Aug 1, 2026 covers ~3 months past the fix-deploy window — long enough for
 * any sporadic user (one who logs in only every few weeks) to have had at
 * least one post-fix login.
 */
const PURGE_CUTOFF_MS = Date.UTC(2026, 7, 1); // Aug 1, 2026 UTC (month is 0-indexed)

const LOCK_NAME = 'outlaw:autosave-purge';

/**
 * Drain the autosave IndexedDB store ONCE per firm/user, ever.
 *
 * The autosave store is crash-recovery infrastructure: it holds in-progress edits so a user
 * who closes the browser mid-edit doesn't lose their work. Do NOT invoke this from any
 * recurring code path — only from a one-time-ever bootstrap to clear the historical bloat
 * left behind by the prior key-mismatch leak (saga's uncacheItem used the wrong parentId,
 * so entries were never removed on save and accumulated indefinitely).
 *
 * Four layers of fail-safe:
 *   1. **Date cutoff (`PURGE_CUTOFF_MS`).** After this date, the function is a no-op
 *      for everyone. Bloat-clearing is one-shot remediation; keeping the destructive
 *      code path live forever risks future regressions.
 *   2. **Cross-tab Web Lock (`outlaw:autosave-purge`).** When two tabs in the same
 *      browser session log in concurrently (or one tab is already mid-purge when a
 *      sibling tab arrives), `navigator.locks.request({ifAvailable: true})` lets only
 *      one of them execute the body — the other returns immediately, avoiding a
 *      redundant `clear()` call against IndexedDB. Same pattern as `outlaw:processQueue`.
 *   3. **Per-(firmId, userId) localStorage flag.** Once a purge succeeds, the flag is
 *      set so it cannot fire again. Multi-firm users get one purge per firm/user combo.
 *   4. **Empty-store check.** Before clearing, count the store. If zero, set the flag
 *      and return without clearing. Makes the purge truly idempotent against
 *      legitimate in-flight autosaves whose owner happens to have lost their localStorage
 *      flag (incognito, cleared site data, post-SignUp.tsx `localStorage.clear()`, etc.).
 *
 * Any future purge for a different historical bug must use a new version flag (`v2`, …),
 * never re-use this one.
 */
export async function purgeAutosaveOnce(firmId: uuid, userId: uuid): Promise<void> {
    if (!firmId || !userId) return;

    // Layer 1: hard cutoff. After Aug 2026, the purge code path is dead.
    if (Date.now() >= PURGE_CUTOFF_MS) return;

    const flagKey = `${PURGE_FLAG_PREFIX}.${firmId}.${userId}`;
    // Pre-lock fast-path: if the flag is already set, no need to acquire the
    // lock at all. Common case (>99% of logins after a user's first post-fix
    // login) — keeps `purgeAutosaveOnce` near-zero-cost on the hot path.
    if (localStorage.getItem(flagKey)) return;

    if (!window.storageWorker?.clearAutosaveStore) return;

    // Layer 2: cross-tab serialization. `ifAvailable: true` means "if another
    // tab is mid-purge, skip — don't queue waiting." That's the right call here
    // because the purge is one-shot remediation; if another tab already did the
    // work, this tab's invocation is redundant.
    //
    // Older browsers without navigator.locks fall back to direct execution —
    // the localStorage flag + empty-store check still defend against the
    // concurrent-clear scenario, just less crisply.
    const runBody = async () => {
        // Re-check the flag inside the lock — another tab may have raced through
        // the body and set it before we acquired.
        if (localStorage.getItem(flagKey)) return;

        try {
            // Layer 4: empty-store check. If there's nothing to purge, just record
            // the flag and return — no destructive call. Defends against the
            // scenario where the localStorage flag was cleared (incognito session,
            // SignUp.tsx, manual "Clear site data") but the IndexedDB autosave
            // store contains legitimate in-flight items the user is actively
            // editing. Note: an empty store may still be a fresh incognito session
            // (both stores wiped) where the no-op result is the same.
            if (window.storageWorker!.getAutosaveCount) {
                const count = await window.storageWorker!.getAutosaveCount(firmId, userId);
                if (count === 0) {
                    localStorage.setItem(flagKey, new Date().toISOString());
                    return;
                }
            }

            await window.storageWorker!.clearAutosaveStore(firmId, userId);
            localStorage.setItem(flagKey, new Date().toISOString());
        }
        catch (err) {
            console.error('autosavePurge: failed to clear autosave store', err);
        }
    };

    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
        await navigator.locks.request(LOCK_NAME, {ifAvailable: true}, async (lock) => {
            // ifAvailable returns null when the lock could not be acquired
            // (another tab holds it). Treat that as "another tab is handling
            // it" and skip.
            if (lock === null) return;
            await runBody();
        });
    }
    else {
        // navigator.locks unavailable — execute without serialization. The
        // flag + count-check defenses still hold.
        await runBody();
    }
}

/** Internal exports for unit testing — do not use from production code. */
export const __test = {
    PURGE_CUTOFF_MS,
    PURGE_FLAG_PREFIX,
};
